import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { logger } from '@celito.clients/services';

import {
  msalConfig,
  msalScopes,
  presenceScope,
} from '../../../../apps/web-client/src/authConfig';
import { postLoginLog } from '../hooks/useLoginLog';

class MsalInstanceManager {
  private static instance: MsalInstanceManager | null = null;
  private msalInstance: PublicClientApplication;
  private isLoggingOut = false;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  async initMsal() {
    try {
      await this.msalInstance.initialize();

      const result = await this.msalInstance.handleRedirectPromise();

      // event arg type should be EventType of msal-browser.
      // But it doesn't work for event.payload.account for some reason.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.msalInstance.addEventCallback((event: any) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS
        ) {
          const account = event.payload.account;

          this.msalInstance.setActiveAccount(account);
        }
      });

      const activeAccount = this.msalInstance.getActiveAccount();

      if (activeAccount === null) {
        if (result === null) {
          await this.msalInstance.loginRedirect({
            scopes: [msalScopes],
            prompt: 'consent',
          });
        }

        if (result) {
          const accessToken = await this.getTokenSilently(false);

          postLoginLog({
            isSuccessful: true,
            accessToken: accessToken ?? '',
            requestId: result.requestId,
            userDetails: {
              userName: result.account?.name,
              oid: result.account?.idTokenClaims?.oid,
              tenantId: result.account?.tenantId,
            },
          });
        }
      }

      return this.msalInstance;
    } catch (err) {
      logger.error(err as Error, {
        origin: 'MsalInstanceManager',
      });
    }
  }

  public static getInstance(): MsalInstanceManager {
    if (this.instance === null) {
      this.instance = new MsalInstanceManager();
    }

    return this.instance;
  }

  public getMsalInstance(): PublicClientApplication {
    return this.msalInstance;
  }

  public setLoggingOut(value: boolean) {
    this.isLoggingOut = value;
  }

  public getLoggingOut(): boolean {
    return this.isLoggingOut;
  }

  public async getTokenSilently(isGraphRequest?: boolean) {
    // Don't attempt token acquisition if logout is in progress
    if (this.isLoggingOut) {
      return null;
    }

    const accounts = this.msalInstance.getAllAccounts();
    const account = accounts[0];

    const tokenRequest = {
      scopes: [isGraphRequest ? presenceScope : msalScopes],
      account,
    };

    try {
      const { accessToken } =
        await this.msalInstance.acquireTokenSilent(tokenRequest);

      return accessToken;
    } catch (error: unknown) {
      // Don't trigger logout if already logging out (prevents race conditions)
      if (this.isLoggingOut) {
        return null;
      }

      if (
        error instanceof Error &&
        error.message &&
        (error.message.includes('interaction_required') ||
          error.message.includes('login_required') ||
          error.message.includes('no_account'))
      ) {
        // Set flag before logout to prevent concurrent logout attempts
        this.isLoggingOut = true;
        this.msalInstance.logoutRedirect();
      } else {
        // Log error for diagnostics, do not force logout on transient errors
        if (typeof logger !== 'undefined') {
          logger.error(error as Error, { origin: 'getTokenSilently' });
        }
      }
      return null;
    }
  }
}

export default MsalInstanceManager;
